// ===================================================================
// 🔀 GERENCIADOR DE CRITÉRIOS DE DESEMPATE
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';

export const TiebreakerManager = React.memo(({ user }) => {
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCriterion, setEditingCriterion] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    data_type: 'age',
    config: { min_age: '', subject_name: '', grade_mode: 'best' }
  });
  const [error, setError] = useState('');
  const { addNotification } = useNotifications();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getTiebreakerCriteria(true);
      setCriteria(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Erro ao carregar critérios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  }, [error]);

  const handleConfigChange = useCallback((key, value) => {
    setFormData(prev => ({
      ...prev,
      config: { ...prev.config, [key]: value }
    }));
    if (error) setError('');
  }, [error]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const code = (formData.code || '').trim();
    const description = (formData.description || '').trim();
    const data_type = formData.data_type || 'age';
    if (!code || !description) {
      setError('Preencha código e descrição');
      return;
    }
    const config = {};
    if (data_type === 'age') {
      const min_age = formData.config?.min_age;
      if (min_age !== '' && min_age != null) {
        const n = parseInt(min_age, 10);
        if (isNaN(n) || n < 0) {
          setError('Idade mínima deve ser um número válido');
          return;
        }
        config.min_age = n;
      }
    } else {
      const subject_name = (formData.config?.subject_name || '').trim();
      if (!subject_name) {
        setError('Informe o nome da disciplina');
        return;
      }
      config.subject_name = subject_name;
      config.grade_mode = (formData.config?.grade_mode || 'best') === 'average' ? 'average' : 'best';
    }

    try {
      setLoading(true);
      const payload = { code, description, data_type, config: Object.keys(config).length ? config : undefined };
      if (editingCriterion) {
        const { error: updateError } = await apiClient.updateTiebreakerCriterion(editingCriterion.id, payload);
        if (updateError) throw new Error(updateError);
        addNotification('Sucesso', 'Critério atualizado com sucesso', 'success');
      } else {
        const { error: createError } = await apiClient.createTiebreakerCriterion(payload);
        if (createError) throw new Error(createError);
        addNotification('Sucesso', 'Critério criado com sucesso', 'success');
      }
      setShowModal(false);
      setEditingCriterion(null);
      setFormData({ code: '', description: '', data_type: 'age', config: { min_age: '', subject_name: '', grade_mode: 'best' } });
      loadData();
    } catch (err) {
      setError(err.message || 'Erro ao salvar critério');
    } finally {
      setLoading(false);
    }
  }, [formData, editingCriterion, loadData, addNotification]);

  const handleEdit = useCallback((c) => {
    setEditingCriterion(c);
    setFormData({
      code: c.code || '',
      description: c.description || '',
      data_type: c.data_type || 'age',
      config: {
        min_age: c.config?.min_age ?? '',
        subject_name: c.config?.subject_name || '',
        grade_mode: c.config?.grade_mode === 'average' ? 'average' : 'best'
      }
    });
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Tem certeza que deseja desativar este critério?')) return;
    try {
      setLoading(true);
      const { error: deleteError } = await apiClient.deleteTiebreakerCriterion(id);
      if (deleteError) throw new Error(deleteError);
      addNotification('Sucesso', 'Critério desativado com sucesso', 'success');
      loadData();
    } catch (err) {
      addNotification('Erro', err.message || 'Erro ao desativar critério', 'error');
    } finally {
      setLoading(false);
    }
  }, [loadData, addNotification]);

  const handleNew = useCallback(() => {
    setEditingCriterion(null);
    setFormData({ code: '', description: '', data_type: 'age', config: { min_age: '', subject_name: '', grade_mode: 'best' } });
    setShowModal(true);
  }, []);

  const getTypeLabel = (data_type, config) => {
    if (data_type === 'age') {
      const min = config?.min_age;
      return min != null ? `Idade (>${min} anos)` : 'Idade (geral)';
    }
    const subj = config?.subject_name;
    const mode = config?.grade_mode === 'average' ? 'Média' : 'Nota';
    return subj ? `${mode}: ${subj}` : 'Nota';
  };

  if (loading && criteria.length === 0) {
    return <LoadingSpinner text="Carregando critérios..." />;
  }

  return (
    <div style={styles.contentContainer}>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>🔀 Critérios de desempate</h2>
          <button onClick={handleNew} style={{ ...styles.button, ...styles.buttonPrimary }}>
            ➕ Novo critério
          </button>
        </div>
        <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: '16px' }}>
          Cadastre critérios para desempate na classificação (idade ou nota de disciplina). Depois, configure a ordem no cadastro do processo.
        </p>

        {error && !showModal && (
          <Alert message={error} type="error" onClose={() => setError('')} />
        )}

        {criteria.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔀</div>
            <h3>Nenhum critério cadastrado</h3>
            <p>Cadastre critérios de desempate (ex.: maior idade, média em Língua Portuguesa)</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.tableHeaderCell}>Código</th>
                  <th style={styles.tableHeaderCell}>Descrição</th>
                  <th style={styles.tableHeaderCell}>Tipo</th>
                  <th style={styles.tableHeaderCell}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {criteria.map(c => (
                  <tr key={c.id} style={styles.tableRow}>
                    <td style={styles.tableCell}>
                      <span style={{ fontWeight: '600', color: '#1F2937' }}>{c.code}</span>
                    </td>
                    <td style={styles.tableCell}>{c.description}</td>
                    <td style={styles.tableCell}>{getTypeLabel(c.data_type, c.config)}</td>
                    <td style={styles.tableCell}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleEdit(c)}
                          style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={() => handleDelete(c.id)}
                          style={{ ...styles.button, ...styles.buttonDanger, ...styles.buttonSmall }}
                        >
                          🗑️ Desativar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingCriterion ? 'Editar critério' : 'Novo critério'}>
        {error && (
          <div style={{ marginBottom: '16px' }}>
            <Alert message={error} type="error" onClose={() => setError('')} />
          </div>
        )}
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Código *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value)}
                style={styles.input}
                placeholder="Ex: age_over_60"
                disabled={!!editingCriterion}
              />
              {editingCriterion && (
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>Código não pode ser alterado</p>
              )}
            </div>
          </div>
          <div style={{ marginTop: '16px' }}>
            <label style={styles.label}>Descrição *</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
              placeholder="Ex: Maior idade entre candidatos com mais de 60 anos"
              required
              rows={3}
            />
          </div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Tipo *</label>
              <select
                value={formData.data_type}
                onChange={(e) => handleInputChange('data_type', e.target.value)}
                style={styles.input}
              >
                <option value="age">Idade</option>
                <option value="grade">Nota de disciplina</option>
              </select>
            </div>
          </div>
          {formData.data_type === 'age' && (
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Idade mínima (opcional)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.config?.min_age ?? ''}
                  onChange={(e) => handleConfigChange('min_age', e.target.value)}
                  style={styles.input}
                  placeholder="Ex: 60 (deixe vazio para idade geral)"
                />
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Se preenchido, considera apenas candidatos com idade superior (ex.: 60 = Estatuto do Idoso)
                </p>
              </div>
            </div>
          )}
          {formData.data_type === 'grade' && (
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Nome da disciplina *</label>
                <input
                  type="text"
                  value={formData.config?.subject_name ?? ''}
                  onChange={(e) => handleConfigChange('subject_name', e.target.value)}
                  style={styles.input}
                  placeholder="Ex: Língua Portuguesa"
                  required={formData.data_type === 'grade'}
                />
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Deve corresponder ao nome da disciplina no cadastro do curso
                </p>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Critério de comparação *</label>
                <select
                  value={formData.config?.grade_mode || 'best'}
                  onChange={(e) => handleConfigChange('grade_mode', e.target.value)}
                  style={styles.input}
                >
                  <option value="best">Maior nota (melhor nota em qualquer série)</option>
                  <option value="average">Média (média das notas em todas as séries)</option>
                </select>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button type="button" onClick={() => setShowModal(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>
              Cancelar
            </button>
            <button type="submit" disabled={loading} style={{ ...styles.button, ...styles.buttonPrimary, opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Salvando...' : (editingCriterion ? 'Atualizar' : 'Criar')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
});
